import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useTranslation } from 'react-i18next';
import { colors, fonts } from 'components/styles/variables';
import { useValidation } from 'lib/hooks/useValidation';
import { buildLoginFormSchema } from 'lib/validationSchemas';
import { formCss, errorCss, inputCss, footerCss } from './styles';
import Separator from './Separator';
import Header from './Header';
import SubmitButton from './SubmitButton';
import StageWrapper from './StageWrapper';

interface Props {
  onSubmit(values: { email: string; password: string }): void | Promise<void>;
  onForgotPasswordClick(): void;
}

export default function LoginByEmail({ onSubmit, onForgotPasswordClick }: Props): JSX.Element {
  const { t } = useTranslation();
  const { validateEmail } = useValidation();
  const schema = buildLoginFormSchema({ emailValidator: validateEmail });

  return (
    <StageWrapper>
      <Header>
        <h1>{t('auth.logIn.headline')}</h1>
        <Separator />
      </Header>
      <Formik
        initialValues={{ email: '', password: '' }}
        validationSchema={schema}
        onSubmit={async (values, { setSubmitting }): Promise<void> => {
          await onSubmit(values);

          setSubmitting(false);
        }}
      >
        {({ isSubmitting }): JSX.Element => (
          <Form className={formCss.className} data-testid="login-by-email-form">
            <label>{t('auth.common.email')}</label>
            <Field
              type="email"
              name="email"
              placeholder="email@email.com"
              className={inputCss.className}
            />
            <div className={`error ${errorCss.className}`}>
              <ErrorMessage name="email">
                {(error): string => {
                  return t(error);
                }}
              </ErrorMessage>
            </div>
            <label>{t('auth.common.password')}</label>
            <Field type="password" name="password" className={inputCss.className} />
            <div className={`error ${errorCss.className}`}>
              <ErrorMessage name="password">
                {(error): string => {
                  return t(error);
                }}
              </ErrorMessage>
            </div>

            <SubmitButton isSubmitting={isSubmitting}>{t('auth.logIn.logIn')}</SubmitButton>
          </Form>
        )}
      </Formik>
      <button className={`footer ${footerCss.className}`} onClick={onForgotPasswordClick}>
        {t('auth.resetPassword.forgotPassword')}
      </button>
      {formCss.styles}
      {inputCss.styles}
      {errorCss.styles}
      {footerCss.styles}
      <style jsx>{`
        .login-by-email {
          align-items: center;
          display: flex;
          flex-direction: column;
          font-family: ${fonts.default};
        }

        label {
          color: ${colors.BLACK};
          font-size: 13px;
          font-weight: 600;
          height: 17px;
          text-align: left;
          text-transform: uppercase;
        }
      `}</style>
    </StageWrapper>
  );
}
